import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";

interface ChoiceBoardAdvancedData {
  preferredLearningStyles?: string[];
  difficultyDistribution?: string;
  selectionRequirements?: string;
  learningOutcomesFocus?: string[];
}

export const LearningStylesStep: React.FC<
  StepComponentProps<ChoiceBoardAdvancedData>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showCustomStyle, setShowCustomStyle] = useState(false);
  const [customStyleText, setCustomStyleText] = useState("");

  const predefinedStyles = [
    {
      id: "visual_kinesthetic",
      emoji: "👁️🤸",
      label: t("choiceBoards.learningStyles.visualKinesthetic"),
      desc: t("choiceBoards.learningStyles.visualKinestheticDesc"),
    },
    {
      id: "analytical_creative",
      emoji: "🧮🎨",
      label: t("choiceBoards.learningStyles.analyticalCreative"),
      desc: t("choiceBoards.learningStyles.analyticalCreativeDesc"),
    },
    {
      id: "all_styles",
      emoji: "🎯",
      label: t("choiceBoards.learningStyles.allStyles"),
      desc: t("choiceBoards.learningStyles.allStylesDesc"),
    },
  ];

  const selectedStyles = data.preferredLearningStyles || [];

  const handleStyleToggle = (styleId: string) => {
    const style = predefinedStyles.find((s) => s.id === styleId);
    const styleLabel = style ? style.label : styleId;

    const updatedStyles = selectedStyles.includes(styleLabel)
      ? selectedStyles.filter((style) => style !== styleLabel)
      : [...selectedStyles, styleLabel];

    onDataChange({ preferredLearningStyles: updatedStyles });
  };

  const handleCustomStyleAdd = () => {
    if (customStyleText.trim()) {
      const updatedStyles = [...selectedStyles, customStyleText.trim()];
      onDataChange({ preferredLearningStyles: updatedStyles });
      setCustomStyleText("");
      setShowCustomStyle(false);
    }
  };

  const isStyleSelected = (styleId: string) => {
    const style = predefinedStyles.find((s) => s.id === styleId);
    const styleLabel = style ? style.label : styleId;
    return selectedStyles.includes(styleLabel);
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("choiceBoards.steps.learningStyles.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("choiceBoards.steps.learningStyles.description")}
        </p>
      </div>

      {/* Style Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("choiceBoards.form.learningStyles")}
          </label>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t("choiceBoards.form.selectMultiple")}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-3">
          {predefinedStyles.map((style) => (
            <div
              key={style.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                isStyleSelected(style.id)
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleStyleToggle(style.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                    isStyleSelected(style.id)
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {isStyleSelected(style.id) && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{style.emoji}</span>
                    <h3 className="font-medium text-base">{style.label}</h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {style.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Custom Style */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("choiceBoards.form.customStyle")}
          </label>
          {!showCustomStyle && (
            <button
              type="button"
              onClick={() => setShowCustomStyle(true)}
              className={`text-sm text-${themeColor}-600 dark:text-${themeColor}-400 hover:text-${themeColor}-700 dark:hover:text-${themeColor}-300 font-medium`}
            >
              {t("choiceBoards.form.addCustom")}
            </button>
          )}
        </div>

        {showCustomStyle && (
          <div className="flex gap-3">
            <input
              type="text"
              value={customStyleText}
              onChange={(e) => setCustomStyleText(e.target.value)}
              placeholder={t("choiceBoards.form.customStylePlaceholder")}
              className={`flex-1 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-3 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
            />
            <button
              type="button"
              onClick={handleCustomStyleAdd}
              disabled={!customStyleText.trim()}
              className={`px-4 py-3 rounded-xl bg-${themeColor}-600 text-white font-medium hover:bg-${themeColor}-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200`}
            >
              {t("common.add")}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCustomStyle(false);
                setCustomStyleText("");
              }}
              className="px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 font-medium hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200"
            >
              {t("common.cancel")}
            </button>
          </div>
        )}
      </div>

      {/* Selected Styles Summary */}
      {selectedStyles.length > 0 && (
        <div className="space-y-3">
          <label
            className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
          >
            {t("choiceBoards.form.selectedStyles")} ({selectedStyles.length})
          </label>
          <div className="flex flex-wrap gap-2">
            {selectedStyles.map((style, index) => (
              <div
                key={index}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-${themeColor}-100 dark:bg-${themeColor}-900/30 text-${themeColor}-700 dark:text-${themeColor}-300 text-sm`}
              >
                <span>{style}</span>
                <button
                  type="button"
                  onClick={() => {
                    const styleObj = predefinedStyles.find(
                      (s) => s.label === style,
                    );
                    if (styleObj) {
                      handleStyleToggle(styleObj.id);
                    } else {
                      // Handle custom style removal
                      const updatedStyles = selectedStyles.filter(
                        (s) => s !== style,
                      );
                      onDataChange({ preferredLearningStyles: updatedStyles });
                    }
                  }}
                  className={`text-${themeColor}-500 hover:text-${themeColor}-700 dark:hover:text-${themeColor}-200`}
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
